import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import Delete from '@mui/icons-material/Delete'
import Add from '@mui/icons-material/Add'
import Lock from '@mui/icons-material/Lock'
import API from '../../../../services/API.js'
import { GlobalContext, TenantSettingsContext } from '../../../../App.js'

const EditableLabel = React.memo(function EditableLabel({ value, onCommit }) {
  const [localValue, setLocalValue] = useState(value)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  return (
    <TextField
      variant="outlined"
      size="small"
      fullWidth
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => onCommit(localValue)}
    />
  )
})

// Editable checklist (conditions / immunisations / fitness / COVID risks).
// Items keep any extra fields they arrived with (e.g. the `key` that marks
// auto-selectable COVID risk items) so edits don't strip them. Every item in
// these lists is inherently notable, so concerning answers always raise a
// review flag - there is no per-item flag choice.
const ChecklistEditor = ({ title, helperText, items, onChange }) => {
  const [newLabel, setNewLabel] = useState('')

  const handleAdd = () => {
    if (!newLabel.trim()) return
    onChange([...items, { label: newLabel.trim() }])
    setNewLabel('')
  }

  return (
    <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
      <CardHeader
        title={title}
        subheader={helperText}
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
      />
      <Divider />
      <CardContent>
        <Stack spacing={1}>
          {items.map((item, i) => (
            <Stack
              key={item.key ?? i}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
            >
              <EditableLabel
                value={item.label}
                onCommit={(newValue) =>
                  onChange(
                    items.map((x, idx) =>
                      idx === i ? { ...x, label: newValue } : x,
                    ),
                  )
                }
              />
              {item.key && (
                <Tooltip title="This item can be set automatically (see the toggles above), so keep its meaning if you reword it.">
                  <Lock fontSize="small" color="disabled" />
                </Tooltip>
              )}
              <IconButton
                size="small"
                color="error"
                onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              >
                <Delete />
              </IconButton>
            </Stack>
          ))}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1}
          >
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Add a new item"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAdd()
                }
              }}
            />
            <IconButton size="small" color="success" onClick={handleAdd}>
              <Add />
            </IconButton>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

const CustomQuestionsEditor = ({ items, onChange }) => {
  const [newQuestion, setNewQuestion] = useState('')
  const [newFlagOn, setNewFlagOn] = useState('Yes')

  const handleAdd = () => {
    if (!newQuestion.trim()) return
    onChange([...items, { question: newQuestion.trim(), flagOn: newFlagOn }])
    setNewQuestion('')
    setNewFlagOn('Yes')
  }

  return (
    <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
      <CardHeader
        title="Additional Questions"
        subheader="Your organisation's own Yes/No questions, asked at the end of the screening."
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
      />
      <Divider />
      <CardContent>
        <Stack spacing={1}>
          {items.map((item, i) => (
            <Stack
              key={i}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
            >
              <EditableLabel
                value={item.question}
                onCommit={(newValue) =>
                  onChange(
                    items.map((x, idx) =>
                      idx === i ? { ...x, question: newValue } : x,
                    ),
                  )
                }
              />
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <Select
                  value={item.flagOn ?? 'Yes'}
                  onChange={(e) =>
                    onChange(
                      items.map((x, idx) =>
                        idx === i ? { ...x, flagOn: e.target.value } : x,
                      ),
                    )
                  }
                >
                  <MenuItem value="Yes">Flag if answered "Yes"</MenuItem>
                  <MenuItem value="No">Flag if answered "No"</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                size="small"
                color="error"
                onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              >
                <Delete />
              </IconButton>
            </Stack>
          ))}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1}
          >
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Add a new question"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAdd()
                }
              }}
            />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select
                value={newFlagOn}
                onChange={(e) => setNewFlagOn(e.target.value)}
              >
                <MenuItem value="Yes">Flag if answered "Yes"</MenuItem>
                <MenuItem value="No">Flag if answered "No"</MenuItem>
              </Select>
            </FormControl>
            <IconButton size="small" color="success" onClick={handleAdd}>
              <Add />
            </IconButton>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

const AUTO_TOGGLES = [
  {
    key: 'autoAgeRisk',
    label: 'Set "Aged over 50" automatically from the employee\'s date of birth',
  },
  {
    key: 'autoPregnancyRisk',
    label:
      'Set "Currently pregnant" automatically from the pregnancy question above',
  },
  {
    key: 'autoBmiRisk',
    label: 'Set "BMI over 40" automatically from the calculated BMI',
  },
]

const OccupationalHealthSettings = () => {
  const [loading, setLoading] = useState(true)
  const [changesPresent, setChangesPresent] = useState(false)
  const [settings, setSettings] = useState({
    enabled: true,
    allowSelfRecomplete: true,
    autoAgeRisk: true,
    autoPregnancyRisk: true,
    autoBmiRisk: true,
    conditions: [],
    immunisations: [],
    fitnessChecks: [],
    covidRisks: [],
    customQuestions: [],
  })

  const { setToast } = useContext(GlobalContext)
  const { setOrgConfig } = useContext(TenantSettingsContext)

  useEffect(() => {
    API.get('/settings?select=occupationalHealthSettings').then((res) => {
      if (res.status === 200 && res.data?.occupationalHealthSettings) {
        setSettings((prev) => ({
          ...prev,
          ...res.data.occupationalHealthSettings,
        }))
      }
      setLoading(false)
    })
  }, [])

  const update = (changes) => {
    setSettings((prev) => ({ ...prev, ...changes }))
    setChangesPresent(true)
  }

  const handleSaveChanges = () => {
    API.put('/site-config', { occupationalHealthSettings: settings }).then(
      (res) => {
        if (res.status === 200) {
          setToast({ message: 'Changes Saved' })
          setChangesPresent(false)
          setOrgConfig((prev) => ({ ...prev, orgConfig: res.data }))
        }
      },
    )
  }

  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="title2" fontWeight={700}>
          Occupational Health Screening
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure the occupational health questionnaire staff complete when
          they join and on a recurring basis. Concerning answers — declared
          conditions, missing immunisations, failed fitness checks, and
          selected COVID-19 risks — are automatically flagged to HR on the
          employee's profile.
        </Typography>
        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </Stack>

      <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title="General"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled !== false}
                  onChange={(e) => update({ enabled: e.target.checked })}
                />
              }
              label={
                settings.enabled !== false
                  ? 'Occupational health screening is enabled'
                  : 'Occupational health screening is disabled'
              }
            />
            {settings.enabled === false && (
              <Alert severity="info">
                Staff will not be asked to complete screenings and screening
                status will not appear on dashboards or block shift bookings.
              </Alert>
            )}
            <Alert severity="info">
              Occupational health screening is part of the appraisals process:
              at the start of each appraisal, staff review their previous
              screening and either confirm nothing has changed or submit an
              update, so there is no separate screening frequency to set. A
              standalone screening is only required from staff who have never
              completed one, or when you request a new one from their profile.
            </Alert>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.allowSelfRecomplete !== false}
                  onChange={(e) =>
                    update({ allowSelfRecomplete: e.target.checked })
                  }
                />
              }
              label="Allow staff to recomplete their screening at any time (not just when due)"
            />
            <Alert severity="info">
              To stop staff booking shifts while their screening is incomplete
              or overdue, use the option under Configuration &gt; Shifts.
            </Alert>
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title="Automatic COVID-19 Risk Answers"
          subheader="These risks can be filled in from information AmbuNet already holds instead of asking the employee."
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={1}>
            {AUTO_TOGGLES.map((toggle) => (
              <FormControlLabel
                key={toggle.key}
                control={
                  <Switch
                    checked={settings[toggle.key] !== false}
                    onChange={(e) => update({ [toggle.key]: e.target.checked })}
                  />
                }
                label={toggle.label}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      <ChecklistEditor
        title="Medical Conditions"
        helperText='Staff are asked "Do you have any of the following?" Ticked conditions are flagged for review.'
        items={settings.conditions ?? []}
        onChange={(items) => update({ conditions: items })}
      />

      <ChecklistEditor
        title="Immunisations"
        helperText="Staff are asked whether they have been immunised/vaccinated against each of these, and can upload supporting evidence. Missing immunisations are flagged for review."
        items={settings.immunisations ?? []}
        onChange={(items) => update({ immunisations: items })}
      />

      <ChecklistEditor
        title="Fitness Requirements"
        helperText='Staff are asked "Can you complete the following?" Anything they cannot complete is flagged for review.'
        items={settings.fitnessChecks ?? []}
        onChange={(items) => update({ fitnessChecks: items })}
      />

      <ChecklistEditor
        title="Individual COVID-19 Risk Assessment"
        helperText="Staff select any risks that apply to them so you are aware of their specific needs. Selected risks are flagged for review."
        items={settings.covidRisks ?? []}
        onChange={(items) => update({ covidRisks: items })}
      />

      <CustomQuestionsEditor
        items={settings.customQuestions ?? []}
        onChange={(items) => update({ customQuestions: items })}
      />

      <Paper
        elevation={3}
        sx={{
          position: 'sticky',
          bottom: 0,
          p: 2,
          borderRadius: 3,
          background: '#ffffffcc',
          backdropFilter: 'blur(10px)',
          border: '1px solid #eee',
        }}
      >
        <Button
          size="large"
          variant="contained"
          fullWidth
          color="success"
          onClick={handleSaveChanges}
          disabled={!changesPresent}
        >
          Save Changes
        </Button>
      </Paper>
    </Stack>
  )
}

export default OccupationalHealthSettings
